import { useState, useEffect, useRef } from 'react';
import { IoAddCircleOutline } from 'react-icons/io5';
import { MdCreateNewFolder, MdUploadFile } from 'react-icons/md';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import PrimaryButtonPurple from '@CommonRepresentationalDisplay/Buttons/PrimaryButtonPurple';
import CreateFolderForm from '@ProjectFunctionalDisplay/CreateFolderForm';
import FileService from '@Helpers/URLHelpers/FileService';
import ErrorHandler from '@Root/ErrorHandler';
import './CreateFolder.scss';

export default function CreateFolder(props) {

    const [showCreateFolderForm, setShowCreateFolderForm] = useState(false);
    const [showCreateFileOption, setShowCreateFileOption] = useState(false);
    const [anchorCreateFileOption, setAnchorCreateFileOption] = useState(null);
    const [, setFiles] = useState(null);
    const inputRef = useRef();
    const { uploadFileInS3 } = useReduxFileStorage();
    const { currentProjectId } = useReduxProject();
    const { activeUserId } = useReduxActiveUser();
    const fileService = new FileService();
    const errorHandler = new ErrorHandler();
    const className = 'createFolder';

    useEffect(() => {

    }, [])

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!!file) {
            let fileSize = file.size / (1024 * 1024);
            if (fileSize > 2) {
                props.fileSizeError();
            } else {
                const url = await convertFileToBase64(file);
                let newFileObj = {
                    file: url,
                    fileName: file.name,
                    fileSize: fileSize,
                    fileType: file.type,
                    projectId: currentProjectId
                }
                setFiles(newFileObj);
                await handleSubmit(newFileObj)
            }
        }
    };

    const handleSubmit = async (newFileObj) => {
        props.setCursor('wait');
        let tableType = props.navigationParentId === 0 ? 'PROJECT' : 'FOLDER';
        let parentId = props.navigationParentId === 0 ? currentProjectId : props.navigationParentId;
        if (props.navigationParent === 'kaiziqResolveConnectionFolder') {
            tableType = 'kaiziqResolveConnectionFolder';
        }
        let file = newFileObj;
        try {
            let body = {
                'fileName': file.fileName,
                'tableType': tableType,
                'itemId': parentId,
                'file': file,
                'projectId': currentProjectId,
                'userId': activeUserId,
                'storeType': 'attachment'
            }
            await uploadFileInS3(body);
            setFiles(null);
            inputRef.current.value = '';
            handleCreateFileOptionPopup();
            await props.renderData(props.navigationParentId);
        } catch (err) {
            errorHandler.logError('File Attachment', err);
        }
        props.setCursor('auto');
    }

    const handleChoose = (event) => {
        inputRef.current.click();
    };

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const handleCreateFileOptionPopup = (event) => {
        if (!showCreateFileOption) {
            if (!!event) {
                setAnchorCreateFileOption(event.currentTarget);
                setShowCreateFileOption(true);
            }
        } else {
            setAnchorCreateFileOption(null);
            setShowCreateFileOption(false);
        }
    }

    const handleCreateFolderForm = () => {
        setShowCreateFolderForm(!showCreateFolderForm);
        handleCreateFileOptionPopup();
    }

    return (
        <div className={className}>
            <PrimaryButtonPurple
                onClick={handleCreateFileOptionPopup}
                startIcon={<IoAddCircleOutline />}
                text="New"
                tooltip="Create new folder or upload files"
                tooltipPlacement="bottom"
            />

            {showCreateFileOption && (
                <Menu
                    open={showCreateFileOption}
                    onClose={handleCreateFileOptionPopup}
                    anchorEl={anchorCreateFileOption}
                    className={className + '__menu'}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                        elevation: 8,
                        sx: {
                            overflow: 'visible',
                            mt: 1.5,
                            '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    }}>
                    <div className={className + '__menu__header'}>
                        <IoAddCircleOutline className={className + '__menu__header__icon'} />
                        <div>
                            <div className={className + '__menu__header__title'}>Create New</div>
                            <div className={className + '__menu__header__subtitle'}>Choose what to create</div>
                        </div>
                    </div>

                    <Divider sx={{ my: 1 }} />

                    <MenuItem
                        onClick={handleCreateFolderForm}
                        className={className + '__menu__item'}>
                        <ListItemIcon className={className + '__menu__item__icon'}>
                            <MdCreateNewFolder />
                        </ListItemIcon>
                        <ListItemText
                            primary="New Folder"
                            secondary="Create an empty folder"
                            primaryTypographyProps={{
                                className: className + '__menu__item__primary'
                            }}
                            secondaryTypographyProps={{
                                className: className + '__menu__item__secondary'
                            }}
                        />
                    </MenuItem>

                    <MenuItem
                        onClick={handleChoose}
                        className={className + '__menu__item'}>
                        <ListItemIcon className={className + '__menu__item__icon'}>
                            <MdUploadFile />
                        </ListItemIcon>
                        <ListItemText
                            primary="Upload File"
                            secondary="Max 2MB per file"
                            primaryTypographyProps={{
                                className: className + '__menu__item__primary'
                            }}
                            secondaryTypographyProps={{
                                className: className + '__menu__item__secondary'
                            }}
                        />
                        <input
                            ref={inputRef}
                            type='file'
                            accept="audio/aac, audio/mp4, audio/mpeg, audio/amr, audio/ogg, audio/opus, application/vnd.ms-powerpoint, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/pdf, text/plain, application/vnd.ms-excel, image/jpeg, image/png, image/webp, video/mp4, video/3gpp"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                    </MenuItem>
                </Menu>
            )}

            {showCreateFolderForm && (
                <CreateFolderForm
                    open={showCreateFolderForm}
                    onClose={handleCreateFolderForm}
                    renderData={props.renderData}
                    navigationParent={props.navigationParent}
                    navigationParentId={props.navigationParentId}
                />
            )}
        </div>
    )
}